import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
    db,
    collection,
    query,
    orderBy,
    limit,
    startAfter,
} from "../firebase";
import { doc, deleteDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { ShoppingCart, Package, TrendingUp } from "lucide-react";

/* ───────── StatsBar ───────── */
const StatsBar = React.memo(({ sales30Count, totalUnits, top5 }) => {
    const animSales = useCountUp(sales30Count);
    const animUnits = useCountUp(totalUnits);



    return (
        <div className="grid gap-4 sm:grid-cols-3 mb-6">
            <Card icon={<ShoppingCart size={40} />} label="Últimas ventas" value={animSales} />
            <Card icon={<Package size={40} />} label="Unidades vendidas" value={animUnits} />
            {/* Top 5 */}
            <div className="card bg-[#2E8F42] shadow-xl text-gray-900">
                <div className="card-body p-4">
                    <div className="flex items-center space-x-3 mb-2">
                        <TrendingUp size={30} className="opacity-80" />
                        <p className="text-sm opacity-80">Top 5 productos</p>
                    </div>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                        {(top5 ?? []).map(({ name, qty, sales }) => (
                            <li key={name}>
                                <span className="font-semibold">{qty}×</span>{" "}
                                {name.length > 32 ? name.slice(0, 29) + "…" : name}
                                <span className="opacity-70"> – {sales} ventas</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
});
const Card = ({ icon, label, value }) => (
    <div className="card bg-[#2E8F42] shadow-xl text-gray-900">
        <div className="card-body p-4 flex-row items-center space-x-4">
            {icon}
            <div>
                <p className="text-sm opacity-80">{label}</p>
                <h2 className="text-3xl font-bold">{value}</h2>
            </div>
        </div>
    </div>
);

/* ───────── helpers ───────── */
const compare = (a, b, key, dir) => {
    const rawA = a?.[key];
    const rawB = b?.[key];
    if (rawA instanceof Date && rawB instanceof Date) {
        const diff = rawA - rawB;
        return dir === "asc" ? diff : -diff;
    }
    const numA = parseFloat(rawA);
    const numB = parseFloat(rawB);
    if (!isNaN(numA) && !isNaN(numB)) return dir === "asc" ? numA - numB : numB - numA;
    const strA = (rawA ?? "").toString().toLowerCase();
    const strB = (rawB ?? "").toString().toLowerCase();
    return dir === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
};

export const useCountUp = (value, duration = 800) => {
    const [display, setDisplay] = useState(value);
    const prev = useRef(value);
    useEffect(() => {
        if (prev.current === value) return;
        prev.current = value;
        let current = 0;
        const step = Math.ceil(value / (duration / 16));
        const id = setInterval(() => {
            current += step;
            if (current >= value) {
                current = value;
                clearInterval(id);
            }
            setDisplay(current);
        }, 16);
        return () => clearInterval(id);
    }, [value, duration]);
    return display;
};

const parseFechaHora = (f, h) => {
    if (!f || f.length !== 8) return new Date();
    const [y, m, d] = [+f.slice(0, 4), +f.slice(4, 6) - 1, +f.slice(6, 8)];
    const [hh = 0, mm = 0, ss = 0] = (h ?? "00:00:00").split(":").map(Number);
    return new Date(y, m, d, hh, mm, ss);
};

const fmtDate = (d) =>
    d.toLocaleString("es-AR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });

/* ───────── pantalla ───────── */
export default function VentasScreen() {
    const [sales, setSales] = useState([]);
    const [initialSales, setInitialSales] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: "fechaHora", dir: "desc" });

    /* modal */
    const [showModal, setShowModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);

    const SUC_NAMES = { "1": "Moscovich", "2": "Guberman", "3": "Sunchales" };

    /* ---------- carga inicial ---------- */
    useEffect(() => {
        fetchInitialSales();
    }, []);

    const mapDoc = (snap) => {
        const data = snap.data();
        const fechaHora = parseFechaHora(data.fecha, data.hora);
        const totalUnits = (data.productos || []).reduce(
            (acc, p) => acc + Number(p.cantidad || 0),
            0
        );
        return { id: snap.id, ...data, fechaHora, totalUnits, sucName: SUC_NAMES[data.sucursal] ?? data.sucursal };
    };

    const fetchInitialSales = async () => {
        try {
            setLoading(true);
            const q = query(collection(db, "ventas"), orderBy("fecha", "desc"), limit(500));
            const snap = await (await import("firebase/firestore")).getDocs(q);
            if (!snap.empty) {
                const arr = snap.docs
                    .map(mapDoc)
                    .filter((d) => Array.isArray(d.productos) && d.productos.length);
                setSales(arr);
                setInitialSales(arr);
                setLastDoc(snap.docs[snap.docs.length - 1]);
                if (snap.size < 500) setHasMore(false);
            } else setHasMore(false);
        } catch (e) {
            console.error(e);
            toast.error("Error al cargar ventas");
        } finally {
            setLoading(false);
        }
    };

    const fetchMoreSales = async () => {
        if (!lastDoc) return;
        try {
            setLoading(true);
            const q = query(
                collection(db, "ventas"),
                orderBy("fecha", "desc"),
                startAfter(lastDoc),
                limit(500)
            );
            const snap = await (await import("firebase/firestore")).getDocs(q);
            if (!snap.empty) {
                const arr = snap.docs
                    .map(mapDoc)
                    .filter((d) => Array.isArray(d.productos) && d.productos.length);
                setSales((p) => [...p, ...arr]);
                setInitialSales((p) => [...p, ...arr]);
                setLastDoc(snap.docs[snap.docs.length - 1]);
                if (snap.size < 500) setHasMore(false);
            } else setHasMore(false);
        } catch (e) {
            console.error(e);
            toast.error("Error al cargar más ventas");
        } finally {
            setLoading(false);
        }
    };

    /* ---------- filtros/orden ---------- */
    const filteredBase = useMemo(() => {
        const t = searchTerm.toLowerCase();
        return sales.filter(
            (s) =>
                s.nombreCliente?.toLowerCase().includes(t) ||
                s.telefonoCliente?.toLowerCase().includes(t) ||
                s.numeroCompra?.toLowerCase().includes(t) ||
                s.nomVendedor?.toLowerCase().includes(t) ||
                s.sucName?.toLowerCase().includes(t) ||
                String(s.id).includes(t)
        );
    }, [sales, searchTerm]);

    const filteredSales = useMemo(
        () => [...filteredBase].sort((a, b) => compare(a, b, sortConfig.key, sortConfig.dir)),
        [filteredBase, sortConfig]
    );

    /* ---------- estadísticas ---------- */
    const stats = useMemo(() => {
        const now = Date.now();
        const THIRTY = 30 * 24 * 60 * 60 * 1000;

        const sales30Count = filteredBase.filter(
            (s) => now - s.fechaHora.getTime() <= THIRTY
        ).length;

        const totalUnits = filteredBase.reduce((acc, s) => acc + s.totalUnits, 0);

        const qtyCounter = {};
        const saleCounter = {};
        filteredBase.forEach((s) =>
            (s.productos || []).forEach((p) => {
                const name = p.descripcionx || p.descripcion;       // ★ CAMBIO 1
                const qty = Number(p.cantidad || 0);
                qtyCounter[name] = (qtyCounter[name] || 0) + qty;
                saleCounter[name] = (saleCounter[name] || 0) + 1;
            })
        );

        const top5 = Object.entries(qtyCounter)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([name, qty]) => ({ name, qty, sales: saleCounter[name] }));

        return { sales30Count, totalUnits, top5 };
    }, [filteredBase]);

    const handleSort = (key) =>
        setSortConfig((p) =>
            p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
        );

    const th = (label, key) => (
        <th className="cursor-pointer select-none" onClick={() => handleSort(key)}>
            {label}
            {sortConfig.key === key && (sortConfig.dir === "asc" ? " ▲" : " ▼")}
        </th>
    );

    /* ---------- UI ---------- */
    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Ventas</h1>

            <StatsBar {...stats} />

            {/* búsqueda */}
            <div className="form-control mb-4">
                <label className="label">
                    <span className="label-text font-bold">Buscar venta</span>
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="text"
                        placeholder="Cliente, vendedor, teléfono, compra…"
                        className="input input-bordered w-full sm:flex-1"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button
                        className="btn bg-gray-400 border-none text-white"
                        onClick={() => {
                            setSearchTerm("");
                            handleSort("fechaHora");
                        }}
                        disabled={loading}
                    >
                        Limpiar
                    </button>
                    {hasMore && initialSales.length > 0 && (
                        <button
                            className="btn bg-[#2E8F42] border-none text-white"
                            onClick={fetchMoreSales}
                            disabled={loading}
                        >
                            {loading ? "Cargando…" : "Cargar más"}
                        </button>
                    )}
                </div>
            </div>

            {/* tabla */}
            <div className="bg-base-100 p-4 rounded shadow">
                {filteredSales.length ? (
                    <table className="table w-full">
                        <thead>
                            <tr>
                                {th("Número compra", "numeroCompra")}
                                {th("Fecha", "fechaHora")}
                                {th("Sucursal", "sucName")}           {/* ★ CAMBIO 2 */}
                                {th("Vendedor", "nomVendedor")}
                                {th("Cliente", "nombreCliente")}
                                {th("Teléfono", "telefonoCliente")}
                                {th("Unidades", "totalUnits")}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSales.map((s) => (
                                <tr
                                    key={s.id}
                                    className="cursor-pointer hover:bg-base-200"
                                    onClick={() => {
                                        setSelectedSale(s);
                                        setShowModal(true);
                                    }}
                                >
                                    <td>{s.numeroCompra}</td>
                                    <td>{fmtDate(s.fechaHora)}</td>
                                    <td>{s.sucName}</td>                {/* ★ muestra sucursal */}
                                    <td>{s.nomVendedor || "--"}</td>
                                    <td>{s.nombreCliente || "--"}</td>
                                    <td>{s.telefonoCliente || "--"}</td>
                                    <td>{s.totalUnits}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No hay ventas para mostrar.</p>
                )}
            </div>

            {/* modal producto */}
            {showModal && selectedSale && (
                <dialog className="modal modal-open">
                    <div className="modal-box max-w-4xl">
                        <h3 className="font-bold text-lg mb-2">
                            Venta #{selectedSale.numeroCompra}
                        </h3>

                        <p className="mb-4">
                            <strong>Cliente:</strong> {selectedSale.nombreCliente}{" "}
                            {selectedSale.telefonoCliente}
                        </p>
                        <p className="mb-2">
                            <strong>Vendedor:</strong> {selectedSale.nomVendedor || "–"}
                        </p>
                        <p className="mb-2">
                            <strong>Fecha & Hora:</strong> {fmtDate(selectedSale.fechaHora)}
                        </p>
                        <p className="mb-4">
                            <strong>Sucursal:</strong> {selectedSale.sucName}
                        </p>

                        <div className="overflow-x-auto">
                            <table className="table w-full">
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th>Cantidad</th>
                                        <th>Laboratorio</th>
                                        <th>Stock actual</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedSale.productos?.map((p, i) => (
                                        <tr key={i}>
                                            <td>{p.descripcionx || p.descripcion}</td>  {/* ★ CAMBIO 1 */}
                                            <td>{p.cantidad}</td>
                                            <td>{p.laboratorio || "–"}</td>
                                            <td>{p.stockActual}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="modal-action">
                            <button className="btn" onClick={() => setShowModal(false)}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </dialog>
            )}
        </div>
    );
}